import { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatApiError, formatINRShort } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Search } from "lucide-react";
import { downloadCSV, inr, Kpi, EmptyRow } from "./utils";

export default function InventoryValueReport() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [bucket, setBucket] = useState("all");

  const load = () => {
    setBusy(true);
    api.get("/reports/inventory/value")
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setBusy(false));
  };
  useEffect(() => { load(); }, []);

  const rows = useMemo(() => {
    if (!data) return [];
    let list = data.rows;
    if (bucket !== "all") list = list.filter((r) => r.age_bucket === bucket);
    if (q.trim()) {
      const n = q.toLowerCase();
      list = list.filter((r) =>
        (r.name || "").toLowerCase().includes(n) ||
        (r.sku || "").toLowerCase().includes(n) ||
        (r.paper_code || "").toLowerCase().includes(n)
      );
    }
    return list;
  }, [data, q, bucket]);

  const exportCSV = () => downloadCSV(
    "inventory-value-age",
    ["SKU", "Name", "Category", "Type", "Paper Code", "Unit", "Current Stock", "Unit Cost", "Stock Value", "Rolls", "Roll Width", "Supplier", "Last Purchase", "Age Days", "Age Bucket"],
    rows.map((r) => [
      r.sku, r.name, r.category, r.type, r.paper_code, r.unit,
      r.current_stock, r.unit_cost, r.stock_value, r.no_of_rolls, r.roll_width,
      r.supplier, r.last_purchase_at, r.age_days, r.age_bucket,
    ])
  );

  const buckets = data?.value_by_age_bucket || {};
  return (
    <div>
      <div className="border border-neutral-200 bg-white p-4 mb-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-64">
          <label className="label-caps block mb-1">Search SKU / name / paper code</label>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-2.5 text-neutral-400" />
            <Input data-testid="invval-search" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 rounded-sm" placeholder="e.g. FASSON, SW8604MM" />
          </div>
        </div>
        <div>
          <label className="label-caps block mb-1">Age bucket</label>
          <Select value={bucket} onValueChange={setBucket}>
            <SelectTrigger data-testid="invval-bucket" className="rounded-sm w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All ages</SelectItem>
              <SelectItem value="0-30">0-30 days</SelectItem>
              <SelectItem value="31-60">31-60 days</SelectItem>
              <SelectItem value="61-90">61-90 days</SelectItem>
              <SelectItem value="91-180">91-180 days</SelectItem>
              <SelectItem value="180+">180+ days</SelectItem>
              <SelectItem value="unknown">Unknown</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button data-testid="invval-refresh" onClick={load} disabled={busy} variant="outline" className="rounded-sm">
          {busy ? "Loading…" : "Refresh"}
        </Button>
        <Button data-testid="invval-export" onClick={exportCSV} className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00]">
          Download .csv
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Kpi label="Total materials" value={data?.totals?.materials_count || 0} />
        <Kpi label="Total stock value" value={formatINRShort(data?.totals?.stock_value || 0)} />
        <Kpi label="Total rolls in stock" value={inr(data?.totals?.rolls || 0)} />
        <Kpi label="Aged 90+ days value" value={formatINRShort((buckets["91-180"] || 0) + (buckets["180+"] || 0))} tone="warn" />
      </div>

      {/* Aging bucket summary */}
      <div className="border border-neutral-200 bg-white p-4 mb-4">
        <div className="label-caps mb-2 text-[10px]">Stock value by age bucket</div>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-sm">
          {["0-30", "31-60", "61-90", "91-180", "180+", "unknown"].map((b) => (
            <div key={b} className={`p-2 border ${bucket === b ? "border-[#FF5A00] bg-orange-50" : "border-neutral-200"} cursor-pointer`}
              onClick={() => setBucket(bucket === b ? "all" : b)}
              data-testid={`invval-bucket-tile-${b}`}>
              <div className="label-caps text-[10px] text-neutral-500">{b === "unknown" ? "Unknown" : `${b} days`}</div>
              <div className="font-mono-num text-lg font-semibold" title={`₹${inr(buckets[b] || 0)}`}>{formatINRShort(buckets[b] || 0)}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="border border-neutral-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="invval-table">
          <thead className="bg-neutral-50 label-caps">
            <tr>
              <th className="text-left px-4 py-2.5">SKU</th>
              <th className="text-left px-4 py-2.5">Name</th>
              <th className="text-left px-4 py-2.5">Type / Paper Code</th>
              <th className="text-right px-4 py-2.5">Stock</th>
              <th className="text-right px-4 py-2.5">Unit Cost</th>
              <th className="text-right px-4 py-2.5">Stock Value</th>
              <th className="text-right px-4 py-2.5">Rolls</th>
              <th className="text-left px-4 py-2.5">Last Purchase</th>
              <th className="text-right px-4 py-2.5">Age (days)</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <EmptyRow cols={9} msg="No materials in stock for this filter." />}
            {rows.map((r) => (
              <tr key={r.sku} className="border-t border-neutral-100 data-row" data-testid={`invval-row-${r.sku}`}>
                <td className="px-4 py-2.5 font-mono-num text-xs">{r.sku}</td>
                <td className="px-4 py-2.5">{r.name}<div className="text-[10px] text-neutral-400 uppercase">{r.category}</div></td>
                <td className="px-4 py-2.5 text-xs">
                  {r.type || "—"}
                  {r.paper_code && <div className="text-neutral-400 font-mono-num">{r.paper_code}</div>}
                </td>
                <td className="px-4 py-2.5 text-right font-mono-num">{inr(r.current_stock)} <span className="text-[10px] text-neutral-500">{r.unit}</span></td>
                <td className="px-4 py-2.5 text-right font-mono-num text-neutral-600">₹{inr(r.unit_cost)}</td>
                <td className="px-4 py-2.5 text-right font-mono-num font-semibold">₹{inr(r.stock_value)}</td>
                <td className="px-4 py-2.5 text-right font-mono-num">{r.no_of_rolls || "—"}</td>
                <td className="px-4 py-2.5 text-xs text-neutral-600">{r.last_purchase_at ? r.last_purchase_at.slice(0, 10) : "—"}</td>
                <td className={`px-4 py-2.5 text-right font-mono-num ${
                  r.age_bucket === "180+" ? "text-rose-700 font-semibold" :
                  r.age_bucket === "91-180" ? "text-amber-700" :
                  "text-neutral-700"
                }`}>
                  {r.age_days ?? "—"}
                  <div className="text-[10px] text-neutral-500 uppercase">{r.age_bucket}</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================================
// 3. Consumption vs Purchase
// ============================================================================
